import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { categoryColor, categoryLabel, formatDateTime, formatRelative, stripMentionIds } from "../lib/constants";
import { Send, ExternalLink, Trash2, X, Phone, User, Webhook, CheckCircle2, AlertCircle, Clock, Mail, MessageSquare, Pencil, CheckSquare, Square, ChevronDown, Plus, ListChecks, MessagesSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../components/Confirm";

const STATUS_TABS = [
  { key: "all",       label: "All" },
  { key: "new",       label: "New" },
  { key: "exported",  label: "Exported" },
  { key: "contacted", label: "Contacted" },
  { key: "qualified", label: "Qualified" },
  { key: "closed",    label: "Closed" },
  { key: "rejected",  label: "Rejected" },
];

export default function Leads() {
  const confirm = useConfirm();
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [focusLead, setFocusLead] = useState(null);
  const [settings, setSettings] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = activeTab !== "all" ? { status: activeTab } : {};
      const [r, s] = await Promise.all([
        api.get("/leads", { params }),
        api.get("/leads/settings"),
      ]);
      setLeads(r.data.leads || []);
      setCounts(r.data.counts || {});
      setSettings(s.data);
    } catch {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); setSelectedIds(new Set()); /* eslint-disable-next-line */ }, [activeTab]);

  // ── Multi-select helpers ─────────────────────────────────────────────────
  const toggleOne = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const visibleIds = leads.map((l) => l.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someSelected = !allSelected && visibleIds.some((id) => selectedIds.has(id));
  const toggleAll = () => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (allSelected) visibleIds.forEach((id) => next.delete(id));
    else visibleIds.forEach((id) => next.add(id));
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());

  // ── Bulk actions ─────────────────────────────────────────────────────────
  const bulkSetStatus = async (status) => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/leads/bulk", {
        action: "set_status", status, ids: Array.from(selectedIds),
      });
      toast.success(`${r.data.updated} lead${r.data.updated === 1 ? "" : "s"} → ${status}`);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally { setBulkBusy(false); }
  };
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const ok = await confirm({
      title: `Delete ${selectedIds.size} lead${selectedIds.size === 1 ? "" : "s"}?`,
      message: "Each lead and its delivery history will be removed permanently.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/leads/bulk", {
        action: "delete", ids: Array.from(selectedIds),
      });
      toast.success(`${r.data.deleted} deleted`);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally { setBulkBusy(false); }
  };

  const resend = async (lead) => {
    try {
      const r = await api.post(`/leads/${lead.id}/resend`);
      const channels = r.data.channels || {};
      const success = Object.entries(channels).filter(([, v]) => v.status === "success").map(([k]) => k);
      const failed = Object.entries(channels).filter(([, v]) => v.status === "failed").map(([k]) => k);
      if (success.length) toast.success(`Sent via ${success.join(", ")}`);
      if (failed.length) toast.error(`Failed: ${failed.join(", ")}`);
      if (!success.length && !failed.length) {
        toast.warning(r.data.reason || "Nothing delivered (no channel enabled)");
      }
      await load();
    } catch {
      toast.error("Resend failed");
    }
  };

  const updateStatus = async (lead, status) => {
    try {
      await api.patch(`/leads/${lead.id}`, { status });
      toast.success(`Marked ${status}`);
      await load();
      if (focusLead?.id === lead.id) {
        const r = await api.get(`/leads/${lead.id}`);
        setFocusLead(r.data);
      }
    } catch {
      toast.error("Update failed");
    }
  };

  const saveMobile = async (leadId, mobile) => {
    try {
      const r = await api.patch(`/leads/${leadId}/mobile`, { mobile });
      toast.success("Mobile saved");
      setLeads((prev) => prev.map((x) => (x.id === leadId ? { ...x, ...r.data } : x)));
      if (focusLead?.id === leadId) setFocusLead((prev) => ({ ...prev, ...r.data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      throw e;
    }
  };

  const removeLead = async (lead) => {
    const ok = await confirm({
      title: "Delete this lead?",
      message: "The lead and its delivery history will be removed permanently.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Deleted");
      setFocusLead(null);
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const anyChannel = settings && settings.enabled && (
    (settings.webhook_enabled && settings.webhook_url) ||
    (settings.slack_enabled && settings.slack_webhook_url) ||
    (settings.email_enabled && (settings.email_recipients || []).length > 0)
  );
  const noWebhook = !anyChannel;

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="leads-page">
      <div className="flex items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-[32px] font-extrabold tracking-tight text-slate-900 leading-tight">Leads</h1>
          <p className="text-[14px] text-slate-500 mt-1">
            Messages that matched your lead-detection rules. Push them to any CRM via webhook.
          </p>
        </div>
        <Link
          to="/app/settings"
          className="h-10 px-4 inline-flex items-center gap-2 border border-slate-200 hover:border-slate-900 rounded-xl text-[13px] font-medium transition"
        >
          <Webhook size={14} />
          Configure
        </Link>
      </div>

      {settings && !settings.enabled && (
        <div className="mb-5 p-4 border border-amber-200 bg-amber-50 rounded-xl text-[13px] text-amber-900">
          <span className="font-semibold">Lead detection is disabled.</span>{" "}
          Enable it in <Link to="/app/settings" className="underline">Settings → Lead Detection</Link> to start capturing leads.
        </div>
      )}
      {settings && settings.enabled && !anyChannel && (
        <div className="mb-5 p-4 border border-blue-200 bg-blue-50 rounded-xl text-[13px] text-blue-900">
          <span className="font-semibold">No delivery channel configured.</span>{" "}
          Leads are being captured but not pushed anywhere. Enable Webhook, Slack, or Email in{" "}
          <Link to="/app/settings" className="underline">Settings</Link>.
        </div>
      )}

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2 mb-5 border-b border-slate-200">
        {STATUS_TABS.map((t) => {
          const count = t.key === "all"
            ? Object.values(counts).reduce((a, b) => a + b, 0)
            : (counts[t.key] || 0);
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2.5 text-[13px] font-semibold border-b-2 transition-all -mb-px ${
                activeTab === t.key
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
              }`}
            >
              {t.label}
              <span className="ml-1.5 text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">Loading…</div>
      ) : leads.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <User size={36} className="mx-auto text-slate-300 mb-3" />
          <div className="text-slate-700 font-semibold mb-1">No leads yet</div>
          <div className="text-slate-500 text-[13px]">When an incoming message matches your rules, it'll appear here.</div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead className="bg-slate-50">
                <tr className="text-left text-[10.5px] tracking-[0.16em] uppercase text-slate-400 font-bold border-b border-slate-200">
                  <th className="pl-4 pr-1 py-2.5 w-8">
                    <button
                      onClick={toggleAll}
                      title={allSelected ? "Clear selection" : "Select all on this page"}
                      className="text-slate-400 hover:text-slate-900 transition"
                    >
                      {allSelected
                        ? <CheckSquare size={15} className="text-blue-600" />
                        : someSelected
                        ? <CheckSquare size={15} className="text-blue-400" />
                        : <Square size={15} />}
                    </button>
                  </th>
                  <th className="px-4 py-2.5">Contact</th>
                  <th className="px-4 py-2.5">Mobile</th>
                  <th className="px-4 py-2.5">Message</th>
                  <th className="px-4 py-2.5">Matched rules</th>
                  <th className="px-4 py-2.5">Captured</th>
                  <th className="px-4 py-2.5">Delivery</th>
                  <th className="px-4 py-2.5 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    className={
                      "border-b last:border-b-0 border-slate-100 hover:bg-slate-50 cursor-pointer " +
                      (selectedIds.has(l.id) ? "bg-blue-50/40" : "")
                    }
                    onClick={() => setFocusLead(l)}
                  >
                    <td
                      className="pl-4 pr-1 py-3 w-8"
                      onClick={(e) => { e.stopPropagation(); toggleOne(l.id); }}
                    >
                      <button className="text-slate-400 hover:text-blue-600 transition">
                        {selectedIds.has(l.id)
                          ? <CheckSquare size={15} className="text-blue-600" />
                          : <Square size={15} />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 truncate max-w-[180px]">{l.sender_name || l.chat_name || "Unknown"}</div>
                      {(l.sender_phone || l.chat_phone) && (
                        <div className="text-[11px] text-slate-500 font-mono flex items-center gap-1 mt-0.5">
                          <Phone size={10} />{l.sender_phone || l.chat_phone}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <MobileEditor lead={l} compact onSave={(m) => saveMobile(l.id, m)} />
                    </td>
                    <td className="px-4 py-3 max-w-md">
                      <div className="text-slate-700 line-clamp-2 text-[12.5px]">{stripMentionIds(l.message_text || "")}</div>
                      {l.category && (
                        <span
                          className="inline-block text-[9.5px] tracking-[0.18em] uppercase font-bold mt-1 px-1.5 py-0.5 border rounded"
                          style={{ borderColor: categoryColor(l.category), color: categoryColor(l.category) }}
                        >
                          {categoryLabel(l.category)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[220px]">
                        {(l.matched_rules || []).slice(0, 3).map((r, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded font-mono">{r}</span>
                        ))}
                        {(l.matched_rules || []).length > 3 && (
                          <span className="text-[10px] text-slate-400">+{l.matched_rules.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[11px] text-slate-500 font-mono whitespace-nowrap">
                      {formatRelative(l.captured_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <DeliveryBadge lead={l} />
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => resend(l)}
                          disabled={noWebhook}
                          title={noWebhook ? "Configure webhook first" : "Resend to CRM"}
                          className="h-7 w-7 grid place-items-center text-slate-400 hover:text-blue-600 disabled:opacity-30 transition"
                        >
                          <Send size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {focusLead && (
        <LeadDrawer
          leadId={focusLead.id}
          onClose={() => setFocusLead(null)}
          onResend={() => resend(focusLead)}
          onStatusChange={(s) => updateStatus(focusLead, s)}
          onSaveMobile={(m) => saveMobile(focusLead.id, m)}
          onDelete={() => removeLead(focusLead)}
          noWebhook={noWebhook}
        />
      )}

      {selectedIds.size > 0 && (
        <BulkActionBar
          count={selectedIds.size}
          busy={bulkBusy}
          onClear={clearSelection}
          onSetStatus={bulkSetStatus}
          onDelete={bulkDelete}
        />
      )}
    </div>
  );
}

// ── Floating bulk action bar ───────────────────────────────────────────────

function BulkActionBar({ count, busy, onClear, onSetStatus, onDelete }) {
  const [stageOpen, setStageOpen] = useState(false);
  const stageRef = useRef(null);
  useEffect(() => {
    if (!stageOpen) return;
    const onClick = (e) => { if (stageRef.current && !stageRef.current.contains(e.target)) setStageOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [stageOpen]);

  const STAGES = [
    { key: "contacted", label: "Contacted", desc: "Reached out / replied" },
    { key: "qualified", label: "Qualified", desc: "Real opportunity" },
    { key: "closed",    label: "Closed",    desc: "Deal won / completed" },
    { key: "rejected",  label: "Rejected",  desc: "Not a fit / no response" },
  ];

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 sw-rise-bounce">
      <div className="flex items-center gap-1 bg-slate-900 text-white rounded-xl shadow-2xl px-2 py-1.5">
        <div className="flex items-center gap-2 px-3 py-1.5 mr-1">
          <ListChecks size={14} className="text-blue-300" />
          <span className="text-[13px] font-semibold">{count} selected</span>
        </div>

        <div ref={stageRef} className="relative">
          <button
            onClick={() => setStageOpen((v) => !v)}
            disabled={busy}
            className="h-8 px-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium rounded-md hover:bg-slate-800 transition disabled:opacity-50"
            data-testid="bulk-move-button"
          >
            Move to stage <ChevronDown size={12} />
          </button>
          {stageOpen && (
            <div className="absolute bottom-full mb-2 right-0 bg-white text-slate-900 border border-slate-200 rounded-lg shadow-xl py-1 min-w-[200px] sw-pop">
              {STAGES.map((s) => (
                <button
                  key={s.key}
                  onClick={() => { setStageOpen(false); onSetStatus(s.key); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50"
                >
                  <div className="text-[13px] font-semibold text-slate-900">{s.label}</div>
                  <div className="text-[11px] text-slate-500">{s.desc}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={onDelete}
          disabled={busy}
          className="h-8 px-3 inline-flex items-center gap-1.5 text-[12.5px] font-medium rounded-md hover:bg-red-600 transition disabled:opacity-50"
          data-testid="bulk-delete-button"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Delete
        </button>

        <div className="w-px h-6 bg-slate-700 mx-1" />

        <button
          onClick={onClear}
          className="h-8 px-2 inline-flex items-center gap-1 text-[12px] text-slate-300 hover:text-white rounded-md hover:bg-slate-800 transition"
          title="Clear selection"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
}

// Inline / detail mobile editor. Empty → input + Save. Set → number + edit.
// Used in two layouts: compact (table cell) and roomy (lead drawer header).
function MobileEditor({ lead, onSave, compact = false }) {
  const [editing, setEditing] = useState(!lead.mobile);
  const [value, setValue] = useState(lead.mobile || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(lead.mobile || "");
    setEditing(!lead.mobile);
  }, [lead.id, lead.mobile]);

  const submit = async () => {
    const v = value.trim();
    if (!v || saving) return;
    setSaving(true);
    try { await onSave(v); setEditing(false); }
    catch { /* parent toasts */ }
    finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="off"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape" && lead.mobile) { setEditing(false); setValue(lead.mobile); }
          }}
          onClick={(e) => e.stopPropagation()}
          placeholder="9876543210"
          className={(compact
            ? "h-7 w-32 px-2 text-[12px]"
            : "h-9 w-44 px-3 text-[13.5px]") +
            " border border-slate-300 focus:border-blue-500 focus:outline-none rounded font-mono"}
        />
        <button
          onClick={(e) => { e.stopPropagation(); submit(); }}
          disabled={!value.trim() || saving}
          className={(compact
            ? "h-7 px-2 text-[11px]"
            : "h-9 px-3 text-[12.5px]") +
            " bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold disabled:opacity-40 transition"}
        >
          {saving ? "…" : "Save"}
        </button>
        {lead.mobile && (
          <button
            onClick={(e) => { e.stopPropagation(); setEditing(false); setValue(lead.mobile); }}
            className="text-slate-400 hover:text-slate-700 text-[11px]"
          >
            cancel
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 group">
      <span className={(compact ? "text-[12.5px]" : "text-[14px]") + " font-mono text-slate-700"}>
        {lead.mobile}
      </span>
      <button
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        title="Edit mobile"
        className="text-slate-300 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition"
      >
        <Pencil size={compact ? 11 : 13} />
      </button>
    </div>
  );
}

// Per-channel delivery badge — derives from lead.deliveries (new schema)
// with a fallback to lead.delivery_status for legacy leads.
const CHANNEL_META = {
  webhook: { label: "Webhook", icon: Webhook,       color: "blue" },
  slack:   { label: "Slack",   icon: MessageSquare, color: "violet" },
  email:   { label: "Email",   icon: Mail,          color: "emerald" },
};

function ChannelBadge({ channel, result }) {
  const meta = CHANNEL_META[channel];
  if (!meta) return null;
  const Icon = meta.icon;
  const status = result?.status;
  if (!status || status === "skipped") return null;
  const tone =
    status === "success" ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
    status === "failed"  ? "bg-red-50 border-red-200 text-red-700" :
                            "bg-amber-50 border-amber-200 text-amber-700";
  return (
    <span
      className={"inline-flex items-center gap-1 text-[10.5px] px-1.5 py-0.5 border rounded font-medium " + tone}
      title={result.error || `${meta.label}: ${status}`}
    >
      <Icon size={10} />
      {meta.label}
      {status === "success" && <CheckCircle2 size={10} />}
      {status === "failed"  && <AlertCircle size={10} />}
    </span>
  );
}

function DeliveryBadge({ lead }) {
  const deliveries = lead.deliveries || {};
  const channels = Object.keys(deliveries).filter((c) =>
    deliveries[c]?.status && deliveries[c].status !== "skipped"
  );
  if (channels.length > 0) {
    return (
      <div className="flex flex-wrap gap-1">
        {channels.map((c) => (
          <ChannelBadge key={c} channel={c} result={deliveries[c]} />
        ))}
      </div>
    );
  }
  // Legacy fallback: single delivery_status field
  const s = lead.delivery_status;
  if (s === "exported") return <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-medium"><CheckCircle2 size={12} />Sent</span>;
  if (s === "failed")   return <span className="inline-flex items-center gap-1 text-[11px] text-red-700 font-medium"><AlertCircle size={12} />Failed</span>;
  if (s === "pending")  return <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 font-medium"><Clock size={12} />Pending</span>;
  return <span className="text-[11px] text-slate-400">—</span>;
}

function LeadDrawer({ leadId, onClose, onResend, onStatusChange, onSaveMobile, onDelete, noWebhook }) {
  const [lead, setLead] = useState(null);

  useEffect(() => {
    api.get(`/leads/${leadId}`).then((r) => setLead(r.data)).catch(() => {});
  }, [leadId]);

  const saveMobileLocal = async (mobile) => {
    await onSaveMobile(mobile);
    const r = await api.get(`/leads/${leadId}`);
    setLead(r.data);
  };

  if (!lead) {
    return (
      <div className="fixed inset-0 z-40">
        <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
        <aside className="absolute right-0 top-0 h-full w-full max-w-xl bg-white border-l border-slate-200 p-6">Loading…</aside>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-slate-900/30" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-xl bg-white border-l border-slate-200 overflow-y-auto sw-fade-up">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Lead detail</div>
          <div className="flex gap-2">
            <button
              onClick={onResend}
              disabled={noWebhook}
              className="h-8 px-3 inline-flex items-center gap-1.5 text-[12.5px] bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition"
            >
              <Send size={13} /> Resend
            </button>
            <button onClick={onDelete} className="h-8 w-8 grid place-items-center text-slate-400 hover:text-red-600 border border-slate-200 rounded-lg">
              <Trash2 size={14} />
            </button>
            <button onClick={onClose} className="h-8 w-8 grid place-items-center border border-slate-200 hover:border-slate-900 rounded-lg">
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <h2 className="text-[20px] font-bold text-slate-900">{lead.sender_name || lead.chat_name || "Unknown"}</h2>
            <div className="mt-2 flex items-center gap-2 text-[13px] text-slate-600">
              <Phone size={13} className="text-slate-400" />
              <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-400">Mobile</span>
              <MobileEditor lead={lead} onSave={saveMobileLocal} />
            </div>
            {!lead.mobile && (
              <p className="mt-1.5 text-[11.5px] text-amber-700">
                WhatsApp didn't share this contact's number (privacy/@lid).
                Add it manually so the lead can be pushed to your CRM.
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {lead.category && (
              <span className="text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-1 border rounded" style={{ borderColor: categoryColor(lead.category), color: categoryColor(lead.category) }}>
                {categoryLabel(lead.category)}
              </span>
            )}
            {lead.priority && (
              <span className="text-[10px] tracking-[0.18em] uppercase font-bold px-2 py-1 border border-slate-200 text-slate-700 rounded">{lead.priority}</span>
            )}
            <DeliveryBadge lead={lead} />
          </div>

          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5">Original message</div>
            <div className="p-3 bg-slate-50 border border-slate-200 rounded text-[13.5px] whitespace-pre-wrap">{stripMentionIds(lead.message_text || "")}</div>
            <div className="text-[11px] text-slate-400 mt-1">Received {formatDateTime(lead.message_received_at)}</div>
          </div>

          {lead.summary && (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5">AI summary</div>
              <p className="text-[13.5px] text-slate-700 italic">"{lead.summary}"</p>
            </div>
          )}

          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5">Matched rules</div>
            <div className="flex flex-wrap gap-1.5">
              {(lead.matched_rules || []).map((r, i) => (
                <span key={i} className="text-[11px] px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded font-mono">{r}</span>
              ))}
            </div>
          </div>

          {lead.deliveries && Object.keys(lead.deliveries).length > 0 ? (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5">
                Delivery (attempts: {lead.delivery_attempts || 0})
              </div>
              <div className="space-y-2">
                {Object.entries(lead.deliveries).map(([channel, result]) => {
                  const meta = CHANNEL_META[channel];
                  if (!meta) return null;
                  const Icon = meta.icon;
                  return (
                    <div key={channel} className="flex items-start gap-2 p-2 bg-slate-50 border border-slate-200 rounded text-[12px]">
                      <Icon size={13} className="mt-0.5 text-slate-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-700">{meta.label}</span>
                          <ChannelBadge channel={channel} result={result} />
                        </div>
                        {result.delivered_at && (
                          <div className="text-[11px] text-slate-500 font-mono mt-0.5">{formatDateTime(result.delivered_at)}</div>
                        )}
                        {result.status_code && <div className="text-[11px] text-slate-500">HTTP {result.status_code}</div>}
                        {result.error && <div className="text-[11px] text-red-700 mt-0.5 break-all">{result.error}</div>}
                        {result.reason && result.status === "skipped" && (
                          <div className="text-[11px] text-slate-400">Skipped: {result.reason}</div>
                        )}
                        {result.sent_payload && (
                          <details className="mt-1">
                            <summary className="text-[10.5px] text-slate-500 cursor-pointer hover:text-slate-700">View sent payload</summary>
                            <pre className="mt-1 p-2 bg-slate-900 text-slate-100 text-[10.5px] rounded overflow-x-auto font-mono leading-relaxed">{JSON.stringify(result.sent_payload, null, 2)}</pre>
                          </details>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : lead.last_delivery && (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5">Last delivery</div>
              <div className="text-[12.5px] text-slate-700">
                <div>At: <span className="font-mono">{formatDateTime(lead.last_delivery.delivered_at)}</span></div>
                <div>Status: {lead.last_delivery.status_code ?? "—"}</div>
                {lead.last_delivery.error && (
                  <div className="text-red-700 mt-1">Error: {lead.last_delivery.error}</div>
                )}
                <div>Attempts: {lead.delivery_attempts || 0}</div>
              </div>
            </div>
          )}

          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5">Status</div>
            <div className="flex flex-wrap gap-1.5">
              {["new", "contacted", "qualified", "closed", "rejected"].map((st) => (
                <button
                  key={st}
                  onClick={() => onStatusChange(st)}
                  className={"text-[12px] px-3 py-1.5 rounded-lg border transition " + (lead.status === st
                    ? "bg-slate-900 text-white border-slate-900"
                    : "border-slate-200 text-slate-700 hover:border-slate-400")}
                >
                  {st}
                </button>
              ))}
            </div>
          </div>

          {/* Follow-up notes — timestamped, append-only thread */}
          <NotesThread lead={lead} onAdded={async () => {
            const r = await api.get(`/leads/${leadId}`); setLead(r.data);
          }} />

          {/* Spawn one or more follow-up tasks from this lead */}
          <LinkedTasks lead={lead} onCreated={async () => {
            const r = await api.get(`/leads/${leadId}`); setLead(r.data);
          }} />

          {lead.task_id && (
            <Link to={`/app/tasks?focus=${lead.task_id}`} className="inline-flex items-center gap-1.5 text-[12px] text-blue-600 hover:underline">
              <ExternalLink size={12} /> View original task
            </Link>
          )}

          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5">Webhook payload preview</div>
            <pre className="p-3 bg-slate-900 text-slate-100 text-[11px] rounded overflow-x-auto font-mono leading-relaxed">{JSON.stringify(lead.preview_payload || {}, null, 2)}</pre>
          </div>
        </div>
      </aside>
    </div>
  );
}

// ── Follow-up notes ────────────────────────────────────────────────────────

function NotesThread({ lead, onAdded }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const notes = lead.notes_log || [];
  // Surface legacy single-string `notes` field as a pseudo entry so older
  // leads' notes don't suddenly disappear from view after this update.
  const legacy = (lead.notes || "").trim();

  const submit = async (e) => {
    e?.preventDefault();
    if (!text.trim() || saving) return;
    setSaving(true);
    try {
      await api.post(`/leads/${lead.id}/notes`, { text: text.trim() });
      setText("");
      await onAdded?.();
      toast.success("Note added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add note");
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
          Follow-up notes
          {(notes.length > 0 || legacy) && (
            <span className="ml-1.5 text-slate-400">({notes.length + (legacy ? 1 : 0)})</span>
          )}
        </div>
      </div>

      {(notes.length === 0 && !legacy) ? (
        <div className="p-3 bg-slate-50 border border-dashed border-slate-200 rounded-lg text-[12px] text-slate-500 text-center">
          No notes yet. Add the first one below.
        </div>
      ) : (
        <ul className="space-y-2 mb-3">
          {legacy && (
            <li className="p-2.5 border border-slate-200 rounded-lg bg-slate-50/60">
              <div className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-400 mb-1">Legacy note</div>
              <div className="text-[13px] text-slate-700 whitespace-pre-wrap">{legacy}</div>
            </li>
          )}
          {notes.slice().reverse().map((n) => (
            <li key={n.id} className="p-2.5 border border-slate-200 rounded-lg bg-white">
              <div className="text-[10.5px] text-slate-400 flex items-center gap-2 mb-1">
                <span className="font-semibold text-slate-600">{n.author || "Unknown"}</span>
                <span className="font-mono">{formatDateTime(n.created_at)}</span>
              </div>
              <div className="text-[13px] text-slate-700 whitespace-pre-wrap">{n.text}</div>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="flex gap-2">
        <textarea
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit(e); }}
          placeholder="Add a follow-up note… (⌘+Enter to save)"
          className="flex-1 min-h-[64px] p-2.5 border border-slate-200 rounded-lg text-[13px] focus:border-blue-600 outline-none resize-y"
          data-testid="lead-note-input"
        />
        <button
          type="submit"
          disabled={!text.trim() || saving}
          className="h-[64px] px-3.5 inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-[12.5px] font-semibold disabled:opacity-50 shrink-0"
          data-testid="lead-note-save"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <MessagesSquare size={13} />}
          Add note
        </button>
      </form>
    </div>
  );
}

// ── Linked tasks (spawn + list) ───────────────────────────────────────────

function LinkedTasks({ lead, onCreated }) {
  const [showForm, setShowForm] = useState(false);
  const ids = lead.linked_task_ids || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
          Follow-up tasks {ids.length > 0 && <span className="text-slate-400">({ids.length})</span>}
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="text-[12px] inline-flex items-center gap-1 text-blue-600 hover:underline"
            data-testid="lead-create-task-button"
          >
            <Plus size={12} /> Create task
          </button>
        )}
      </div>

      {ids.length > 0 && (
        <ul className="space-y-1.5 mb-2">
          {ids.map((tid) => (
            <li key={tid}>
              <Link
                to={`/app/tasks?focus=${tid}`}
                className="inline-flex items-center gap-1.5 text-[12.5px] text-blue-600 hover:underline"
              >
                <ExternalLink size={11} /> Open task <span className="font-mono text-slate-400 text-[11px]">{tid.slice(0, 8)}…</span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <CreateTaskForm
          lead={lead}
          onCancel={() => setShowForm(false)}
          onCreated={async () => { setShowForm(false); await onCreated?.(); }}
        />
      )}
    </div>
  );
}

function CreateTaskForm({ lead, onCancel, onCreated }) {
  const contact = lead.sender_name || lead.chat_name || "lead";
  const [title, setTitle] = useState(`Follow up with ${contact}`);
  const [priority, setPriority] = useState(lead.priority || "medium");
  const [deadline, setDeadline] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!title.trim() || saving) return;
    setSaving(true);
    try {
      await api.post(`/leads/${lead.id}/task`, {
        title: title.trim(),
        priority,
        deadline: deadline ? new Date(deadline).toISOString() : null,
      });
      toast.success("Task created");
      await onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create task");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="p-3 border border-slate-200 rounded-lg bg-slate-50/60 space-y-2.5">
      <div>
        <label className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-1 block">Title</label>
        <input
          type="text" required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full h-9 px-3 border border-slate-200 rounded text-[13px] focus:border-blue-600 outline-none bg-white"
          autoFocus
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-1 block">Priority</label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="w-full h-9 px-2 border border-slate-200 rounded text-[13px] focus:border-blue-600 outline-none bg-white"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <div>
          <label className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-1 block">Deadline (optional)</label>
          <input
            type="datetime-local"
            step={900}
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className="w-full h-9 px-2 border border-slate-200 rounded text-[13px] focus:border-blue-600 outline-none bg-white"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button" onClick={onCancel}
          className="h-9 px-3 border border-slate-200 hover:border-slate-400 rounded text-[12.5px] text-slate-700"
        >
          Cancel
        </button>
        <button
          type="submit" disabled={!title.trim() || saving}
          className="h-9 px-3.5 inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-[12.5px] font-semibold disabled:opacity-50"
          data-testid="lead-create-task-submit"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
          Create task
        </button>
      </div>
    </form>
  );
}
